import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  DollarSign, Search, Calendar, IndianRupee, Download, Receipt, 
  CheckCircle, Filter, ArrowUpDown, Building2, ChevronDown, ChevronRight,
  Folder, FolderOpen, Eye, Wallet, PieChart
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export default function ClientPaymentHistory() {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [totals, setTotals] = useState({ received: 0, count: 0 });
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [fundLedgers, setFundLedgers] = useState({}); // NEW: Fund ledger per project

  // iter-386u-i — role-aware labels so a client sees "Payments Made" while
  // admin/staff sees "Payments Received from Clients".
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isClient = user.role === 'client';
  const labels = isClient ? {
    title: 'My Payments',
    subtitle: 'Track all payments you have made to date',
    totalCardLabel: 'Total Paid',
    totalCardColor: 'text-blue-700',
    totalCardIconBg: 'bg-blue-100',
    totalCardIconColor: 'text-blue-600',
    badgeText: 'Paid',
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
    rowBg: 'bg-blue-50/50 border-blue-100',
    rowIconBg: 'bg-blue-100',
    rowIcon: 'text-blue-600',
    rowAmount: 'text-blue-700',
  } : {
    title: 'Client Payment Ledger',
    subtitle: 'Track all payments received from clients',
    totalCardLabel: 'Total Received',
    totalCardColor: 'text-green-700',
    totalCardIconBg: 'bg-green-100',
    totalCardIconColor: 'text-green-600',
    badgeText: 'Received',
    badgeClass: 'bg-green-100 text-green-700 border-green-200',
    rowBg: 'bg-green-50/50 border-green-100',
    rowIconBg: 'bg-green-100',
    rowIcon: 'text-green-600',
    rowAmount: 'text-green-700',
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      // Get all projects
      const projectsRes = await axios.get(`${API}/projects`);
      setProjects(projectsRes.data);
      
      // Fetch payment ledger and fund ledger for each project
      const allPayments = [];
      const ledgerData = {};
      
      for (const project of projectsRes.data) {
        try {
          const ledgerRes = await axios.get(`${API}/projects/${project.id}/payment-ledger`);
          if (ledgerRes.data.client_payments) {
            ledgerRes.data.client_payments.forEach(p => {
              if (p.status !== 'cancelled') {
                allPayments.push({
                  ...p,
                  project_id: project.id,
                  project_name: project.name
                });
              }
            });
          }
          
          // Fetch fund ledger (client view)
          try {
            const fundRes = await axios.get(`${API}/projects/${project.id}/fund-ledger/client-view`);
            if (fundRes.data) {
              ledgerData[project.id] = fundRes.data;
            }
          } catch (e) {
            // Fund ledger not available
          }
        } catch (e) {
          // Skip project if ledger fetch fails
        }
      }
      
      setFundLedgers(ledgerData);
      
      // Sort by date descending
      allPayments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setPayments(allPayments);
      
      // Calculate totals
      const totalReceived = allPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      setTotals({ received: totalReceived, count: allPayments.length });
      
      // Projects collapsed by default
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/payments/${paymentId}/receipt-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  // Group payments by project
  const groupedByProject = useMemo(() => {
    const groups = {};
    
    payments.forEach(payment => {
      const matchesSearch = !searchQuery || 
        payment.notes?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.project_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (matchesSearch) {
        if (!groups[payment.project_id]) {
          groups[payment.project_id] = {
            project_id: payment.project_id,
            project_name: payment.project_name,
            payments: [],
            total: 0
          };
        }
        groups[payment.project_id].payments.push(payment);
        groups[payment.project_id].total += payment.amount || 0;
      }
    });
    
    return Object.values(groups);
  }, [payments, searchQuery]);

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedProjects(new Set(projects.map(p => p.id)));
  };

  const collapseAll = () => {
    setExpandedProjects(new Set());
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-payment-history-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <IndianRupee className={labels.totalCardIconColor} size={24} />
              {labels.title}
            </h1>
            <p className="text-stone-500 mt-1">{labels.subtitle}</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${labels.totalCardIconBg} rounded-lg`}>
                  <IndianRupee size={20} className={labels.totalCardIconColor} />
                </div>
                <div>
                  <p className="text-sm text-stone-500">{labels.totalCardLabel}</p>
                  <p className={`text-2xl font-bold ${labels.totalCardColor}`}>₹{totals.received.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Receipt size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Transactions</p>
                  <p className="text-2xl font-bold text-blue-700">{totals.count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Active Projects</p>
                  <p className="text-2xl font-bold text-purple-700">{projects.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search payments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>

        {/* Payments Grouped by Project */}
        <div className="space-y-4">
          {groupedByProject.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-stone-500">
                  <Receipt size={40} className="mx-auto mb-3 text-stone-300" />
                  <p>No payments found</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            groupedByProject.map(group => (
              <Collapsible 
                key={group.project_id} 
                open={expandedProjects.has(group.project_id)}
                onOpenChange={() => toggleProject(group.project_id)}
              >
                <Card className="overflow-hidden">
                  {/* Project Header - Clickable to expand/collapse */}
                  <CollapsibleTrigger asChild>
                    <div className="bg-gradient-to-r from-stone-50 to-white px-4 py-3 cursor-pointer hover:bg-stone-100 transition-colors border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedProjects.has(group.project_id) ? (
                            <FolderOpen size={20} className="text-amber-600" />
                          ) : (
                            <Folder size={20} className="text-amber-600" />
                          )}
                          <div>
                            <h3 className="font-semibold text-stone-900">{group.project_name}</h3>
                            <p className="text-xs text-stone-500">{group.payments.length} payment(s)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-stone-500">{labels.totalCardLabel}</p>
                            <p className={`text-lg font-bold ${labels.totalCardColor}`}>₹{group.total.toLocaleString('en-IN')}</p>
                          </div>
                          {expandedProjects.has(group.project_id) ? (
                            <ChevronDown size={20} className="text-stone-400" />
                          ) : (
                            <ChevronRight size={20} className="text-stone-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  {/* Payments List */}
                  <CollapsibleContent>
                    {/* Fund Ledger Summary */}
                    {fundLedgers[group.project_id] && (
                      <div className="bg-blue-50/50 p-4 border-b border-blue-100">
                        <div className="flex items-center gap-2 mb-3">
                          <Wallet size={16} className="text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">Fund Ledger Summary</span>
                          <Badge variant="outline" className="bg-white text-blue-600 border-blue-200 text-xs ml-auto">
                            <Eye size={10} className="mr-0.5" />
                            View Only
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-3">
                          <div className="bg-white p-2 rounded text-center border">
                            <p className="text-xs text-stone-500">Contract</p>
                            <p className="font-bold text-stone-800">₹{(fundLedgers[group.project_id].contract_value || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="bg-green-50 p-2 rounded text-center border border-green-100">
                            <p className="text-xs text-green-600">Paid</p>
                            <p className="font-bold text-green-700">₹{(fundLedgers[group.project_id].total_paid || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="bg-amber-50 p-2 rounded text-center border border-amber-100">
                            <p className="text-xs text-amber-600">Balance</p>
                            <p className="font-bold text-amber-700">₹{(fundLedgers[group.project_id].balance_due || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="bg-blue-50 p-2 rounded text-center border border-blue-100">
                            <p className="text-xs text-blue-600">Progress</p>
                            <p className="font-bold text-blue-700">{fundLedgers[group.project_id].payment_progress_percentage || 0}%</p>
                          </div>
                        </div>
                        
                        {/* Payment Stages */}
                        {fundLedgers[group.project_id].payment_stages && (
                          <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2">
                            {fundLedgers[group.project_id].payment_stages.map((stage, idx) => (
                              <div 
                                key={idx} 
                                className={`p-2 rounded text-center text-xs ${
                                  stage.paid ? 'bg-green-100 border border-green-200' : 'bg-stone-100 border border-stone-200'
                                }`}
                              >
                                <p className={`font-medium ${stage.paid ? 'text-green-700' : 'text-stone-600'}`}>
                                  {stage.label.split(' ')[0]}
                                </p>
                                <p className={`${stage.paid ? 'text-green-600' : 'text-stone-500'}`}>
                                  {stage.paid ? '✓ Paid' : `${stage.percentage}%`}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="divide-y divide-stone-100">
                      {group.payments.map(payment => (
                        <div key={payment.id} className="p-4 hover:bg-stone-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 ${labels.rowIconBg} rounded-full`}>
                                  <CheckCircle size={16} className={labels.rowIcon} />
                                </div>
                                <div>
                                  <p className={`font-semibold ${labels.rowAmount}`}>₹{(payment.amount || 0).toLocaleString('en-IN')}</p>
                                  <p className="text-xs text-stone-400">
                                    {new Date(payment.created_at).toLocaleString('en-IN')}
                                  </p>
                                </div>
                              </div>
                              {payment.notes && (
                                <p className="text-sm text-stone-600 mt-2 ml-11">"{payment.notes}"</p>
                              )}
                              {payment.recorded_by_name && (
                                <p className="text-xs text-stone-400 mt-1 ml-11">
                                  Recorded by: {payment.recorded_by_name}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadReceipt(payment.id)}
                              className="text-blue-600"
                            >
                              <Download size={14} className="mr-1" />
                              Receipt
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
